import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';
import { handleSendGeneric } from 'services/generic';
import { convertBrl } from 'utility/generic';
import { Notification } from 'notification/notification';

export const ModalAdjustDebtor = ({
  isOpen,
  onToggle,
  clientId,
  saldoAtual,
  onSuccess,
}) => {
  const [confirmZero, setConfirmZero] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [observation, setObservation] = useState('');
  const [loading, setLoading] = useState(false);

  const saldo = Number(saldoAtual) || 0;

  // Limpa os campos sempre que o modal abre
  useEffect(() => {
    if (isOpen) {
      setConfirmZero(false);
      setPaymentAmount('');
      setObservation('');
    }
  }, [isOpen]);

  const toggle = () => {
    setConfirmZero(false);
    setPaymentAmount('');
    setObservation('');
    onToggle();
  };

  const valorDigitado = Math.abs(parseFloat(paymentAmount.replace(',', '.')) || 0);
  // saldo positivo = cliente deve → pagamento reduz; saldo negativo = cliente tem crédito → pagamento reduz o crédito
  const saldoPreview = saldo < 0 ? saldo + valorDigitado : saldo - valorDigitado;

  const previewColor =
    saldoPreview < 0 ? '#2dce89' : saldoPreview === 0 ? '#adb5bd' : '#f5365c';

  const previewLabel =
    saldoPreview < 0
      ? '🟢 Crédito a favor do cliente'
      : saldoPreview === 0
        ? '✅ Saldo zerado'
        : '🔴 Ainda devedor';

  const handleConfirm = async () => {
    console.log('[AdjustDebtor] handleConfirm executado — paymentAmount:', paymentAmount, 'valorDigitado:', valorDigitado);
    if (!paymentAmount || valorDigitado === 0) {
      Notification(400, 'Informe um valor válido para o ajuste!');
      return;
    }
    await submitAdjust(false);
  };

  const handleZero = async () => {
    if (!confirmZero) {
      setConfirmZero(true);
      return;
    }
    await submitAdjust(true);
  };

  const dispararPushNotification = (mensagem) => {
    if (!('Notification' in window)) return;
    if (window.Notification.permission === 'granted') {
      new window.Notification('DNG — Ajuste de Saldo', {
        body: mensagem,
        icon: '/logo192.png',
      });
    } else if (window.Notification.permission !== 'denied') {
      window.Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          new window.Notification('DNG — Ajuste de Saldo', {
            body: mensagem,
            icon: '/logo192.png',
          });
        }
      });
    }
  };

  const submitAdjust = async (zeroed) => {
    setLoading(true);
    try {
      const payload = {
        payment_amount: zeroed ? saldo : valorDigitado,
        observation,
        zeroed,
      };
      console.log('[AdjustDebtor] clientId:', clientId, 'payload:', payload);

      const resultado = await handleSendGeneric(
        `/v1/client/${clientId}/adjust-debtor`,
        'POST',
        payload,
      );

      console.log('[AdjustDebtor] resultado:', resultado);

      if (resultado !== undefined) {
        const mensagem = zeroed
          ? 'Saldo zerado com sucesso!'
          : `Pagamento de ${convertBrl(valorDigitado)} registrado com sucesso!`;
        Notification(200, mensagem);
        dispararPushNotification(mensagem);
        toggle();
        if (onSuccess) onSuccess();
      } else {
        Notification(
          500,
          'Não foi possível ajustar o saldo. Verifique o console.',
        );
      }
    } catch (err) {
      console.error('[AdjustDebtor] erro inesperado:', err);
      Notification(500, 'Erro inesperado ao ajustar o saldo.');
    } finally {
      setLoading(false);
      setConfirmZero(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Ajuste de Saldo Devedor</ModalHeader>

      <ModalBody>
        {/* Saldo atual */}
        <div
          style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 'bold', color: '#525f7f' }}>
            Saldo atual:
          </span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '18px',
              color: saldo > 0 ? '#f5365c' : '#2dce89',
            }}
          >
            {convertBrl(saldo)}
          </span>
        </div>

        {/* Input valor */}
        <FormGroup>
          <Label style={{ fontWeight: 'bold' }}>Valor do pagamento (R$)</Label>
          <Input
            type='text'
            inputMode='decimal'
            placeholder='0,00'
            value={paymentAmount}
            onChange={(e) => {
              console.log('[AdjustDebtor] input onChange:', e.target.value);
              setConfirmZero(false);
              setPaymentAmount(e.target.value);
            }}
            style={{ fontSize: '16px' }}
          />
        </FormGroup>

        {/* Preview do saldo resultante */}
        {paymentAmount !== '' && (
          <div
            style={{
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '10px 16px',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#525f7f', fontSize: '13px' }}>
              {previewLabel}
            </span>
            <span
              style={{
                fontWeight: 'bold',
                fontSize: '17px',
                color: previewColor,
              }}
            >
              {convertBrl(saldoPreview)}
            </span>
          </div>
        )}

        {/* Observação */}
        <FormGroup>
          <Label style={{ fontWeight: 'bold' }}>
            Observação{' '}
            <span style={{ fontWeight: 'normal', color: '#adb5bd' }}>
              (opcional)
            </span>
          </Label>
          <Input
            type='textarea'
            rows={2}
            placeholder='Ex: Pagamento em dinheiro, acerto parcial...'
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            style={{ fontSize: '14px' }}
          />
        </FormGroup>

        {/* Aviso de confirmação de zerar */}
        {confirmZero && (
          <div
            style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '10px 14px',
              color: '#856404',
              fontSize: '14px',
            }}
          >
            ⚠️ Confirma que deseja <strong>zerar todo o saldo</strong> de{' '}
            <strong>{convertBrl(saldo)}</strong>? Clique novamente em "Zerar
            Saldo" para confirmar.
          </div>
        )}

      </ModalBody>

      <ModalFooter style={{ justifyContent: 'space-between' }}>
        {/* Zerar Saldo — lado esquerdo */}
        <Button
          type='button'
          color={confirmZero ? 'danger' : 'secondary'}
          outline={!confirmZero}
          onClick={handleZero}
          disabled={loading || saldo <= 0}
          size='sm'
        >
          {confirmZero ? '⚠️ Confirmar Zeramento' : 'Zerar Saldo'}
        </Button>

        {/* Fechar + Confirmar — lado direito */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type='button'
            color='secondary'
            onClick={toggle}
            disabled={loading}
            size='sm'
          >
            Fechar
          </Button>
          <Button
            type='button'
            color='primary'
            onClick={() => {
              console.log('[AdjustDebtor] btn Confirmar clicado — paymentAmount:', paymentAmount, '| valorDigitado:', valorDigitado, '| loading:', loading);
              handleConfirm();
            }}
            disabled={loading}
            size='sm'
          >
            {loading ? 'Salvando...' : 'Confirmar'}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
